/* eslint-disable max-len */
import 'map.prototype.tojson';
import { CrossSellingGroup } from '@1po/1po-bff-fe-spec/generated/backoffice/cross_selling/response/CrossSellingReferencesFileResponse';
import {
  Reference as DHReference,
  ReferencePlateLine,
  ReferenceType,
} from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/Reference';
import { ReuseAvailability } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/ReuseAvailability';
import { ReuseAvailabilityGroup } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/ReuseAvailabilityGroup';
import { ReuseReference } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/ReuseReference';
import { IAMEquivalentReferences } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/IAMEquivalentReferences';
import { Reference as IAMReference } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/Reference';
import {
  GetIAMReferenceApplicabilityResponse,
  ReferenceApplicability,
} from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/response/GetIAMReferenceApplicabilityResponse';
import { ReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceDiscount';
import { ReferencePrice } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferencePrice';
import { ReferenceRepairPrice } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceRepairPrice';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import {
  ReferenceSource,
  ReferenceStockTradingDataRequestDetail,
} from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferencesStockRequest';
import { ReferenceTradingDataRequestDetail } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferenceTradingDataRequest';
import { ReferencesDiscountsResponse } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/response/ReferencesDiscountsResponse';
import {
  BACKOFFICE_CROSS_SELLING_GET_REFERENCES_FILE_RESPONSE,
  BACKOFFICE_CROSS_SELLING_GET_REFERENCES_RESPONSE,
  CHECK_IF_STOCKS_ARE_UP_TO_DATE_RESPONSE,
  GET_DMS_REFERENCES_STOCKS_RESPONSE,
  GET_EQUIVALENT_REFERENCES_RESPONSE,
  GET_EXADIS_ELIGIBLE_REFERENCES_RESPONSE,
  GET_IAM_REFERENCE_APPLICABILITY_RESPONSE,
  GET_IS_DISCONTINUED_REFERENCE_RESPONSE,
  GET_PLATES_FOR_REFERENCE_RESPONSE,
  GET_REFERENCE_3D_MODEL_RESPONSE,
  GET_REFERENCES_DISCOUNTS_RESPONSE,
  GET_REFERENCES_PRICE_RESPONSE,
  GET_REFERENCES_REPAIR_PRICE_RESPONSE,
  GET_REFERENCES_STOCKS_RESPONSE,
  GET_REUSE_AVAILABILITIES_RESPONSE,
  GET_REUSE_STOCK_RESPONSE,
  REMOVE_REFERENCES_DISCOUNTS_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { DMSReferenceStock } from '@1po/1po-bff-fe-spec/generated/dms/model/DMSReferenceStock';
import { OrderDeliveryType } from '@1po/1po-bff-fe-spec/generated/order/request/CheckoutOrderRequest';
import { createAction, createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { createIAMSupplierReferenceKey } from 'domains/catalog/Catalog.types';
import {
  CHECK_IF_STOCKS_ARE_UP_TO_DATE_REQUEST,
  DHReferenceLocal,
  FETCH_STOCKS,
  GET_CROSS_SELLING_REFERENCES,
  GET_EQUIVALENT_REFERENCES_REQUEST,
  GET_EXADIS_ELIGIBLE_REFERENCES_REQUEST,
  GET_IAM_REFERENCE_APPLICABILITY_REQUEST,
  GET_IS_DISCONTINUED_REFERENCE_REQUEST,
  GET_PLATES_FOR_REFERENCE_REQUEST,
  GET_REFERENCE_3D_MODEL_REQUEST,
  GET_REFERENCES_DISCOUNTS_REQUEST,
  GET_REFERENCES_PRICE_REQUEST,
  GET_REFERENCES_REPAIR_PRICES_REQUEST,
  GET_REFERENCES_STOCKS_REQUEST,
  GET_REUSE_AVAILABILITIES_REQUEST,
  GET_REUSE_STOCKS_REQUEST,
  IAMReferenceLocal,
  LocalTabType,
  NON_MKTP,
  ORIGIN,
  ReferenceCatalog,
  ReferencePriceType,
  REFERENCES_NAMESPACE,
  ReferencesState,
  SNAPSHOT_STOCKS_REQUEST,
} from 'domains/references/References.types';
import {
  FOUND,
  getData,
  getSearchData,
  hasData,
  LOADING,
  mergeObjectInMap,
  mergePricesInMap,
  mergeStocksInSearchDataMap,
  NO_DATA,
  NOT_FOUND,
  SEARCH_STATUS,
  SearchData,
} from 'utils';
import { getLatestInStockSupersessionReference } from './References.utils';

// Saga actions
export const fetchReferencesPriceRequestSaga = createAction<{
  references: ReferenceTradingDataRequestDetail[];
  basketRequest: boolean;
}>(GET_REFERENCES_PRICE_REQUEST);
export const fetchReferencesPriceResponseSaga = createAction(GET_REFERENCES_PRICE_RESPONSE);

export const fetchCrossSellingReferencesRequestSaga = createAction<{
  references: string[];
  vehicleKey: string | undefined;
}>(GET_CROSS_SELLING_REFERENCES);
export const fetchCrossSellingReferencesResponseSaga = createAction(BACKOFFICE_CROSS_SELLING_GET_REFERENCES_RESPONSE);
export const fetchCrossSellingFileReferencesResponseSaga = createAction(
  BACKOFFICE_CROSS_SELLING_GET_REFERENCES_FILE_RESPONSE,
);
export const checkIfStocksAreStillUpToDateRequestSaga = createAction<{
  requestId: string;
  references: {
    referenceNumber: string;
    type: ReferenceType;
    quantity: number;
    deliveryType?: OrderDeliveryType;
    sellerNumber?: string;
    sellerName?: string;
    supplierCode?: string;
    origin?: ORIGIN;
    referenceSource?: ReferenceSource;
    isCertasReference: boolean;
  }[];
}>(CHECK_IF_STOCKS_ARE_UP_TO_DATE_REQUEST);
export const checkIfStocksAreStillUpToDateResponseSaga = createAction(CHECK_IF_STOCKS_ARE_UP_TO_DATE_RESPONSE);
export const snapshotStocksSaga = createAction(SNAPSHOT_STOCKS_REQUEST);
export const fetchReferencesRepairPriceRequestSaga = createAction<{
  references: ReferenceTradingDataRequestDetail[];
}>(GET_REFERENCES_REPAIR_PRICES_REQUEST);
export const fetchReferencesRepairPriceResponseSaga = createAction(GET_REFERENCES_REPAIR_PRICE_RESPONSE);
export const fetchReferencesStocksRequestSaga = createAction<{
  data: ReferenceStockTradingDataRequestDetail[];
  fetchEvenIfExists?: boolean;
}>(GET_REFERENCES_STOCKS_REQUEST);
export const fetchReuseStocksRequestSaga = createAction<{
  referenceNumbers: string[];
  nodeId?: string;
}>(GET_REUSE_STOCKS_REQUEST);
export const fetchReuseAvailabilitiesRequestSaga = createAction<{
  referenceNumbers: string[];
}>(GET_REUSE_AVAILABILITIES_REQUEST);
export const fetchEquivalentReferencesRequestSaga = createAction<{
  reference: string;
}>(GET_EQUIVALENT_REFERENCES_REQUEST);
export const fetchExadisEligibleReferencesRequestSaga = createAction<{
  referenceNumbers: string[];
}>(GET_EXADIS_ELIGIBLE_REFERENCES_REQUEST);
export const fetchReuseStocksResponseSaga = createAction(GET_REUSE_STOCK_RESPONSE);
export const fetchReuseAvailabilitiesResponseSaga = createAction(GET_REUSE_AVAILABILITIES_RESPONSE);
export const fetchEquivalentReferencesResponseSaga = createAction(GET_EQUIVALENT_REFERENCES_RESPONSE);
export const fetchExadisEligibleReferencesResponseSaga = createAction(GET_EXADIS_ELIGIBLE_REFERENCES_RESPONSE);
export const fetchReferencesStocksResponseSaga = createAction(GET_REFERENCES_STOCKS_RESPONSE);
export const dmsReferencesStocksResponseSaga = createAction(GET_DMS_REFERENCES_STOCKS_RESPONSE);
export const fetchReferencesDiscountsRequestSaga = createAction<{
  references: ReferenceTradingDataRequestDetail[];
}>(GET_REFERENCES_DISCOUNTS_REQUEST);
export const fetchReferenceDiscountsResponse = createAction(GET_REFERENCES_DISCOUNTS_RESPONSE);
export const fetchReferenceDiscountsRemoveResponse = createAction(REMOVE_REFERENCES_DISCOUNTS_RESPONSE);
const getNotEmptyArrayOrDefault = <T>(incoming?: Array<T>, old?: Array<T>) =>
  incoming && incoming.length > 0 ? incoming : old;
export const fetchPlatesForReferenceRequestSaga = createAction<{
  vehicleKey: string;
  referenceNumber: string;
}>(GET_PLATES_FOR_REFERENCE_REQUEST);
export const fetchPlatesForReferenceResponseSaga = createAction(GET_PLATES_FOR_REFERENCE_RESPONSE);
export const fetchStocks = createAction<{
  requestStockDetails: ReferenceStockTradingDataRequestDetail[];
}>(FETCH_STOCKS);

export const fetchIsDiscontinuedReferenceRequestSaga = createAction<{
  referenceNumber: string;
}>(GET_IS_DISCONTINUED_REFERENCE_REQUEST);
export const fetchIsDiscontinuedReferenceResponseSaga = createAction(GET_IS_DISCONTINUED_REFERENCE_RESPONSE);

export const fetchIAMReferenceApplicabilityRequestSaga = createAction<{
  reference: IAMReferenceLocal;
}>(GET_IAM_REFERENCE_APPLICABILITY_REQUEST);
export const fetchIAMReferenceApplicabilityResponseSaga = createAction(GET_IAM_REFERENCE_APPLICABILITY_RESPONSE);
export const sendGetReference3dModelRequestSaga = createAction<{
  reference: string;
}>(GET_REFERENCE_3D_MODEL_REQUEST);
export const sendGetReference3dModelResponseSaga = createAction(GET_REFERENCE_3D_MODEL_RESPONSE);

function addDHReference(
  reference: DHReferenceLocal,
  references: Map<string, Draft<DHReferenceLocal | NO_DATA>>,
  detailStatus: SEARCH_STATUS,
): void {
  const localRef = getData(references.get(reference.referenceNumber));
  mergeObjectInMap(references, reference.referenceNumber, {
    ...reference,
    // if exists, do not change it
    additionalInformation: getNotEmptyArrayOrDefault(localRef?.additionalInformation, reference.additionalInformation),
    linkedReferences: getNotEmptyArrayOrDefault(localRef?.linkedReferences, reference.linkedReferences),
    alternativeReferences: getNotEmptyArrayOrDefault(localRef?.alternativeReferences, reference.alternativeReferences),
    referencePlateLines: getNotEmptyArrayOrDefault(localRef?.referencePlateLines, reference.referencePlateLines),
    supersessionChainReferences: getNotEmptyArrayOrDefault(
      localRef?.supersessionChainReferences,
      reference.supersessionChainReferences,
    ),
    replacingReferences: getNotEmptyArrayOrDefault(localRef?.replacingReferences, reference.replacingReferences),
    imageKeys: getNotEmptyArrayOrDefault(localRef?.imageKeys, reference.imageKeys),
    detailStatus: detailStatus ?? localRef?.detailStatus,
    isApplicableToCurrentVehicle: reference.isApplicableToCurrentVehicle ?? localRef?.isApplicableToCurrentVehicle,
    referencePlateLinesStatus:
      localRef?.referencePlateLinesStatus ?? (reference.referencePlateLines?.length ?? 0) > 0 ? FOUND : undefined,
    source: 'dh',
  });
}

function addIAMReference(
  reference: IAMReferenceLocal,
  references: Map<string, Draft<IAMReferenceLocal | NO_DATA>>,
  detailStatus: SEARCH_STATUS,
): void {
  const localRef = getData(references.get(reference.referenceNumber));
  mergeObjectInMap(references, reference.referenceNumber, {
    ...reference,
    // if exists, do not change it
    brand: localRef?.brand ?? reference.brand,
    detailStatus: detailStatus ?? localRef?.detailStatus,
    linkedReferences: getNotEmptyArrayOrDefault(localRef?.linkedReferences, reference.linkedReferences),
    source: 'iam',
  });
  // add ref again into map with supplierCode in key if exists
  if (reference.supplierCode) {
    const supplierKey = createIAMSupplierReferenceKey(reference.referenceNumber, reference.supplierCode);
    const localRefSupplier = getData(references.get(supplierKey));
    mergeObjectInMap(references, supplierKey, {
      ...reference,
      // if exists, do not change it
      brand: localRefSupplier?.brand ?? reference.brand,
      detailStatus: detailStatus ?? localRefSupplier?.detailStatus,
      linkedReferences: getNotEmptyArrayOrDefault(localRefSupplier?.linkedReferences, reference.linkedReferences),
      source: 'iam',
    });
  }
}

function getReferencesState(
  state: Draft<ReferencesState>,
  vehicleKey: string | undefined,
): {
  iamReferences: {
    references: Map<string, IAMReferenceLocal | NO_DATA>;
    vehicleReferences: Map<Draft<string>, Draft<IAMReferenceLocal | NO_DATA>>;
  };
  dhReferences: {
    references: Map<string, DHReferenceLocal | NO_DATA>;
    vehicleReferences: Map<Draft<string>, Draft<DHReferenceLocal | NO_DATA>>;
  };
} {
  if (!vehicleKey) {
    return {
      iamReferences: {
        references: state.references.iamReferences.references,
        vehicleReferences: new Map(),
      },
      dhReferences: {
        references: state.references.dhReferences.references,
        vehicleReferences: new Map(),
      },
    };
  }

  const dhVehicleReferences =
    state.references.dhReferences.vehicleReferences.get(vehicleKey) ??
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    state.references.dhReferences.vehicleReferences.set(vehicleKey, new Map()).get(vehicleKey)!;
  const iamVehicleReferences =
    state.references.iamReferences.vehicleReferences.get(vehicleKey) ??
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    state.references.iamReferences.vehicleReferences.set(vehicleKey, new Map()).get(vehicleKey)!;

  return {
    dhReferences: {
      vehicleReferences: dhVehicleReferences,
      references: new Map(),
    },
    iamReferences: {
      vehicleReferences: iamVehicleReferences,
      references: new Map(),
    },
  };
}

const dhReferences: ReferenceCatalog<DHReferenceLocal> = {
  vehicleReferences: new Map<string, Map<string, DHReferenceLocal | NO_DATA>>(),
  references: new Map<string, DHReferenceLocal | NO_DATA>(),
};

const iamReferences: ReferenceCatalog<IAMReferenceLocal> = {
  vehicleReferences: new Map<string, Map<string, IAMReferenceLocal | NO_DATA>>(),
  references: new Map<string, IAMReferenceLocal | NO_DATA>(),
};

// Init state
export const initialState: ReferencesState = {
  references: {
    dhReferences,
    iamReferences,
  },
  pricesV2: new Map<string, Map<string, ReferencePriceType> | NO_DATA>(),
  repairPrices: new Map<string, SearchData<string>>(),
  stockInfoV2: new Map<string, SearchData<Map<string, ReferenceStock>>>(),
  dmsStockInfo: new Map<string, SearchData<DMSReferenceStock>>(),
  dmsStockUpdateChecks: new Map<string, DMSReferenceStock[] | typeof LOADING>(),
  stockInfoSnapshotV2: new Map<string, SearchData<Map<string, ReferenceStock>>>(),
  stockUpdateChecks: new Map<string, ReferenceStock[] | typeof LOADING>(),
  referenceDiscounts: new Map<string, SearchData<ReferenceDiscount>>(),
  referenceToReferenceType: new Map<string, ReferenceType>(),
  selectedTab: undefined,
  reuseStock: new Map<string, SearchData<ReuseReference>>(),
  reuseAvailabilities: new Map<string, SearchData<ReuseAvailability[]>>(),
  referenceToReferenceSource: new Map<string, ReferenceSource>(),
  equivalentReferences: new Map<string, SearchData<string[]>>(),
  exadisEligibleReferences: { searchStatus: undefined },
  discontinuedReferences: new Map<string, SearchData<boolean>>(),
  referenceApplicabilities: new Map<string, SearchData<ReferenceApplicability[]>>(),
  reference3dModel: new Map<string, SearchData<string>>(),
};

// Saga actions

// Slice
const slice = createSlice({
  name: REFERENCES_NAMESPACE,
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setDHReferences: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string | undefined;
        references: DHReference[];
        detailStatus?: SEARCH_STATUS;
      }>,
    ) => {
      const { vehicleKey, references, detailStatus } = payload;
      const { dhReferences: dhReferenceState } = getReferencesState(state, vehicleKey);
      const referenceState = vehicleKey ? dhReferenceState.vehicleReferences : dhReferenceState.references;
      references.forEach((r) => {
        addDHReference({ ...r, source: 'dh' }, referenceState, detailStatus);
        state.referenceToReferenceType.set(r.referenceNumber, r.type);
      });
    },
    setPlatesForReferenceSearchStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string;
        reference: string;
        status: SEARCH_STATUS;
      }>,
    ) => {
      const { vehicleKey, reference, status } = payload;
      const referenceMap = state.references.dhReferences.vehicleReferences.get(vehicleKey) ?? new Map();
      const curr = getData(referenceMap.get(reference));
      referenceMap.set(reference, {
        ...curr,
        referencePlateLinesStatus: status,
      });
    },
    setPlatesForReference: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string;
        reference: string;
        plates: ReferencePlateLine[];
      }>,
    ) => {
      const { vehicleKey, reference, plates } = payload;
      const referenceMap = state.references.dhReferences.vehicleReferences.get(vehicleKey) ?? new Map();
      const curr = getData(referenceMap.get(reference));
      referenceMap.set(reference, {
        ...curr,
        referencePlateLinesStatus: FOUND,
        referencePlateLines: plates,
      });
    },
    resetReferences: (state) => {
      state.references = initialState.references;
    },
    resetDHReferences: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string;
      }>,
    ) => {
      const { vehicleKey } = payload;
      state.references.dhReferences.vehicleReferences.delete(vehicleKey);
    },
    setIAMReferences: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string | undefined;
        references: IAMReference[] | NO_DATA;
        detailStatus?: SEARCH_STATUS;
      }>,
    ) => {
      const { vehicleKey, references, detailStatus } = payload;
      const { iamReferences: iamReferenceState } = getReferencesState(state, vehicleKey);
      const referenceState = vehicleKey ? iamReferenceState.vehicleReferences : iamReferenceState.references;

      if (references instanceof Array) {
        references?.forEach((r) => {
          addIAMReference({ ...r, source: 'iam', referenceSource: r.referenceSource }, referenceState, detailStatus);
          state.referenceToReferenceType.set(r.referenceNumber, r.type);
          state.referenceToReferenceSource.set(r.referenceNumber, r.referenceSource ?? 'STANDARD');
        });
      }
    },
    setReferencesPriceNoDataStatusV2: (
      state,
      {
        payload,
      }: PayloadAction<{
        referenceNumbers: string[];
        status: NO_DATA;
      }>,
    ) => {
      const { referenceNumbers, status } = payload;
      referenceNumbers.forEach((ref) => {
        const current = state.pricesV2.get(ref);
        if (hasData(current)) {
          return;
        }
        mergeObjectInMap(state.pricesV2, ref, status);
      });
    },
    setReferencesRepairPriceNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        referenceNumbers: string[];
        status: NO_DATA;
      }>,
    ) => {
      const { referenceNumbers, status } = payload;
      referenceNumbers.forEach((ref) => state.repairPrices.set(ref, { searchStatus: status }));
    },
    setReferencesStocksNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        references: string[];
        status: SEARCH_STATUS;
      }>,
    ) => {
      const { references, status } = payload;
      references.forEach((ref) => {
        mergeObjectInMap(state.stockInfoV2, ref, { searchStatus: status });
      });
    },
    setDmsReferencesStocksNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        references: string[];
        status: SEARCH_STATUS;
      }>,
    ) => {
      const { references, status } = payload;
      references.forEach((ref) => {
        mergeObjectInMap(state.dmsStockInfo, ref, { searchStatus: status });
      });
    },
    setReferencesPriceDataV2: (state, { payload }: PayloadAction<ReferencePrice[]>) => {
      payload.forEach((ref) => {
        const objToMerge =
          !ref.currency && !ref.clientView && !ref.garageView
            ? NOT_FOUND
            : new Map<string, ReferencePriceType>().set(ref.sellerNumber ?? NON_MKTP, {
                currency: ref.currency,
                clientView: ref.clientView,
                garageView: ref.garageView,
                sellerName: ref.sellerName,
                sellerNumber: ref.sellerNumber,
              });

        mergePricesInMap(state.pricesV2, ref.reference, objToMerge);
      });
    },
    resetReferencesPriceData: (state) => {
      state.pricesV2 = initialState.pricesV2;
    },
    setReferencesRepairPriceData: (state, { payload }: PayloadAction<ReferenceRepairPrice[]>) => {
      payload.forEach((ref) => state.repairPrices.set(ref.referenceNumber, { searchStatus: FOUND, data: ref.price }));
    },
    setDmsReferencesStocksData: (state, { payload }: PayloadAction<DMSReferenceStock[]>) => {
      payload.forEach((newStock) => {
        const isEmpty = newStock.warehouses === undefined || newStock.warehouses.length === 0;
        mergeObjectInMap(state.dmsStockInfo, newStock.reference, {
          searchStatus: isEmpty ? NOT_FOUND : FOUND,
          data: newStock,
        });
      });
    },
    setReferencesStocksDataV2: (state, { payload }: PayloadAction<ReferenceStock[]>) => {
      payload.forEach((newStock) => {
        const isEmpty = newStock.warehouses === undefined || newStock.warehouses.length === 0;
        const stockEntry: ReferenceStock = {
          reference: newStock.reference,
          confirmedQuantity: newStock.confirmedQuantity,
          warehouses: newStock.warehouses,
          primaryStockResponse: newStock.primaryStockResponse,
          sellerName: newStock.sellerName,
          sellerNumber: newStock.sellerNumber,
        };
        // Only include maxConfirmedQuantity when the backend explicitly provides a real value.
        // Using loose != null guards against both null and undefined, so that a subsequent
        // regular stock re-fetch (which may omit the field or send null) cannot overwrite
        // a valid maxConfirmedQuantity that was set by a stock-validation response
        // (e.g. during the basket quantity-update flow) .
        if (newStock.maxConfirmedQuantity != null) {
          stockEntry.maxConfirmedQuantity = newStock.maxConfirmedQuantity;
        }
        const objToMerge = new Map<string, ReferenceStock>().set(newStock.sellerNumber ?? NON_MKTP, stockEntry);

        mergeStocksInSearchDataMap(state.stockInfoV2, newStock.reference, {
          searchStatus: isEmpty ? NOT_FOUND : FOUND,
          data: objToMerge,
        });
      });
    },

    setReferencesStocksSnapshotData: (state) => {
      state.stockInfoSnapshotV2 = state.stockInfoV2;
    },
    setReferencesDiscountsSearchStatus: (
      state,
      { payload }: PayloadAction<{ referenceNumbers: string[]; searchStatus: SEARCH_STATUS }>,
    ) => {
      const { referenceNumbers, searchStatus } = payload;
      referenceNumbers.forEach((r) => {
        state.referenceDiscounts.set(r, { searchStatus });
      });
    },
    setReferencesDiscounts: (state, { payload }: PayloadAction<ReferencesDiscountsResponse>) => {
      const { referenceDiscounts } = payload;
      referenceDiscounts.forEach((discount) => {
        state.referenceDiscounts.set(discount.referenceNumber, { searchStatus: FOUND, data: discount });
      });
    },
    removeReferencesDiscounts: (state, { payload }: PayloadAction<Array<string>>) => {
      payload.forEach((refNumber) => {
        state.referenceDiscounts.delete(refNumber);
      });
    },
    resetReferencesDiscounts: (state) => {
      state.referenceDiscounts = initialState.referenceDiscounts;
    },
    resetStockData: (state) => {
      state.stockInfoV2 = initialState.stockInfoV2;
      state.dmsStockInfo = initialState.dmsStockInfo;
      state.stockInfoSnapshotV2 = initialState.stockInfoSnapshotV2;
      state.reuseStock = initialState.reuseStock;
    },
    setCrossSellingReferences: (
      state,
      {
        payload,
      }: PayloadAction<{
        crossSellingReferences: CrossSellingGroup[];
        vehicleKey: string | undefined;
        status?: NO_DATA;
      }>,
    ) => {
      const { vehicleKey, crossSellingReferences, status } = payload;
      const { dhReferences: dhReferenceState } = getReferencesState(state, vehicleKey);
      const referenceState = vehicleKey ? dhReferenceState.vehicleReferences : dhReferenceState.references;
      const refNumbers = [...referenceState.keys()];
      refNumbers.forEach((ref) => {
        const localRef = getData(referenceState.get(ref));
        if (localRef) {
          const isRequested = crossSellingReferences.find((cr) => cr.sourceReference === ref);
          const group = crossSellingReferences.find((cr) => cr.sourceReference === ref);
          const newCrossSells = [
            ...new Set((getData(localRef.crossSelling) ?? []).concat(group?.crossSellingReferences ?? [])),
          ];

          const getCrossSellingStatus = () => {
            if (status === LOADING && isRequested) {
              return LOADING;
            }
            if (getData(localRef.crossSelling) || group) {
              return newCrossSells;
            }
            return NOT_FOUND;
          };

          if (status !== LOADING || (status === LOADING && isRequested)) {
            referenceState.set(ref, {
              ...localRef,
              crossSelling: getCrossSellingStatus(),
            });
          }
        }
      });
    },
    setResolveCrossSellingReferencesStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string | undefined;
        status: NO_DATA;
      }>,
    ) => {
      const { status, vehicleKey } = payload;
      const { dhReferences: dhReferenceState } = getReferencesState(state, vehicleKey);
      const referenceState = vehicleKey ? dhReferenceState.vehicleReferences : dhReferenceState.references;
      const refNumbers = [...referenceState.keys()];
      refNumbers.forEach((ref) => {
        const refLocal = getData(referenceState.get(ref));
        if (refLocal?.crossSelling === LOADING) {
          referenceState.set(ref, {
            ...refLocal,
            crossSelling: status,
          });
        }
      });
    },
    setStockUpdateCheck: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: string;
        result: ReferenceStock[] | typeof LOADING;
      }>,
    ) => {
      const { id, result } = payload;
      state.stockUpdateChecks.set(id, result);
    },
    setDmsStockUpdateCheck: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: string;
        result: DMSReferenceStock[] | typeof LOADING;
      }>,
    ) => {
      const { id, result } = payload;
      state.dmsStockUpdateChecks.set(id, result);
    },
    setDHSingleReferenceStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string | undefined;
        reference: string;
        status: NO_DATA;
      }>,
    ) => {
      const { vehicleKey, reference, status } = payload;
      const { dhReferences: dhReferenceState } = getReferencesState(state, vehicleKey);
      const referenceState = vehicleKey ? dhReferenceState.vehicleReferences : dhReferenceState.references;

      if (!hasData(referenceState.get(reference))) {
        referenceState.set(reference, status);
      } else {
        mergeObjectInMap(referenceState, reference, {
          referenceNumber: reference,
          detailStatus: status,
          source: 'dh',
          type: 'STANDARD',
        });
      }
    },
    setReuseStockNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        referenceNumbers: string[];
        status: NO_DATA;
      }>,
    ) => {
      const { referenceNumbers, status } = payload;
      referenceNumbers.forEach((ref) => state.reuseStock.set(ref, { searchStatus: status }));
    },
    setReuseStockData: (state, { payload }: PayloadAction<ReuseReference[]>) => {
      payload.forEach((ref) => {
        const hasData =
          (ref.eco && ref.eco.totalQuantity !== 0) ||
          (ref.medium && ref.medium.totalQuantity !== 0) ||
          (ref.premium && ref.premium.totalQuantity !== 0);
        state.reuseStock.set(ref.referenceNumber, {
          searchStatus: hasData ? FOUND : NOT_FOUND,
          data: hasData ? ref : undefined,
        });
      });
    },
    setReuseAvailabilitiesNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        referenceNumbers: string[];
        status: NO_DATA;
      }>,
    ) => {
      const { referenceNumbers, status } = payload;
      referenceNumbers.forEach((ref) => state.reuseAvailabilities.set(ref, { searchStatus: status }));
    },
    setReuseAvailabilitiesData: (state, { payload }: PayloadAction<ReuseAvailabilityGroup[]>) => {
      payload.forEach((group) => {
        const hasParts = group.parts.length > 0;
        state.reuseAvailabilities.set(group.referenceNumber, {
          searchStatus: hasParts ? FOUND : NOT_FOUND,
          data: hasParts ? group.parts : undefined,
        });
      });
    },
    setEquivalentReferencesNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference: string;
        status: NO_DATA;
      }>,
    ) => {
      state.equivalentReferences.set(payload.reference, {
        searchStatus: payload.status,
      });
    },
    setReferenceApplicabilitiesNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference: string;
        status: NO_DATA;
      }>,
    ) => {
      state.referenceApplicabilities.set(payload.reference, {
        searchStatus: payload.status,
      });
    },
    setEquivalentReferences: (state, { payload }: PayloadAction<IAMEquivalentReferences[]>) => {
      payload.forEach((refs) => {
        state.equivalentReferences.set(refs.mainReference, {
          searchStatus: refs.equivalentReferences.length > 0 ? FOUND : NOT_FOUND,
          data: refs?.equivalentReferences?.map((eqRef) => eqRef.referenceNumber),
        });
        refs.equivalentReferences.forEach((eqRef) =>
          addIAMReference(
            { ...eqRef, source: 'iam', referenceSource: eqRef.referenceSource },
            state.references.iamReferences.references,
            FOUND,
          ),
        );
      });
    },
    setExadisEligibleReferencesSearchStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        status: NO_DATA;
      }>,
    ) => {
      state.exadisEligibleReferences = {
        ...state.exadisEligibleReferences,
        searchStatus: payload.status,
      };
    },
    setExadisEligibleReferences: (state, { payload }: PayloadAction<string[]>) => {
      const existing = state.exadisEligibleReferences.data ?? [];
      state.exadisEligibleReferences = {
        searchStatus: FOUND,
        data: Array.from(new Set([...existing, ...payload])),
      };
    },
    setReferenceApplicabilities: (state, { payload }: PayloadAction<GetIAMReferenceApplicabilityResponse>) => {
      state.referenceApplicabilities.set(payload.referenceNumber, {
        searchStatus: payload.applicabilities.length > 0 ? FOUND : NOT_FOUND,
        data: payload.applicabilities,
      });
    },
    setSelectedTab: (state, action: PayloadAction<LocalTabType>) => {
      state.selectedTab = action.payload;
    },

    setDiscontinuedReferenceSearchStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference: string;
        status: NO_DATA;
      }>,
    ) => {
      state.discontinuedReferences.set(payload.reference, {
        searchStatus: payload.status,
      });
    },
    setDiscontinuedReference: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference: string;
        discontinued: boolean;
      }>,
    ) => {
      state.discontinuedReferences.set(payload.reference, {
        searchStatus: FOUND,
        data: payload.discontinued,
      });
    },
    setReference3dModelSearchStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference: string;
        status: NO_DATA;
      }>,
    ) => {
      state.reference3dModel.set(payload.reference, {
        searchStatus: payload.status,
      });
    },
    setReference3dModel: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference: string;
        model: string;
      }>,
    ) => {
      state.reference3dModel.set(payload.reference, {
        searchStatus: FOUND,
        data: payload.model,
      });
    },
  },
});

// Actions
export const {
  setInitialState,
  setDHReferences,
  setPlatesForReferenceSearchStatus,
  setPlatesForReference,
  resetReferences,
  resetDHReferences,
  setIAMReferences,
  setReferencesPriceNoDataStatusV2,
  setReferencesRepairPriceNoDataStatus,
  setReferencesStocksNoDataStatus,
  setDmsReferencesStocksNoDataStatus,
  setDmsStockUpdateCheck,
  setReferencesPriceDataV2,
  resetReferencesPriceData,
  setReferencesRepairPriceData,
  setDmsReferencesStocksData,
  setReferencesStocksDataV2,
  setDHSingleReferenceStatus,
  setReferencesDiscountsSearchStatus,
  setReferencesDiscounts,
  setStockUpdateCheck,
  removeReferencesDiscounts,
  resetReferencesDiscounts,
  resetStockData,
  setCrossSellingReferences,
  setResolveCrossSellingReferencesStatus,
  setReuseStockNoDataStatus,
  setReuseStockData,
  setReuseAvailabilitiesNoDataStatus,
  setReuseAvailabilitiesData,
  setEquivalentReferencesNoDataStatus,
  setEquivalentReferences,
  setExadisEligibleReferencesSearchStatus,
  setExadisEligibleReferences,
  setSelectedTab,
  setReferencesStocksSnapshotData,
  setDiscontinuedReference,
  setDiscontinuedReferenceSearchStatus,
  setReferenceApplicabilities,
  setReferenceApplicabilitiesNoDataStatus,
  setReference3dModelSearchStatus,
  setReference3dModel,
} = slice.actions;

// Getters/Selectors
export const getDHReference = createSelector(
  (state: RootState) => state.references.references.dhReferences.vehicleReferences,
  (state: RootState) => state.references.references.dhReferences.references,
  (
    _state: RootState,
    payload: {
      vehicleKey: string | undefined;
      referenceNumber?: string;
    },
  ) => payload,
  (vehicleReferences, references, payload) => {
    if (!payload.referenceNumber) {
      return undefined;
    }
    return payload.vehicleKey
      ? vehicleReferences.get(payload.vehicleKey)?.get(payload.referenceNumber)
      : references.get(payload.referenceNumber);
  },
);

export const getDHReferences = createSelector(
  (state: RootState) => state.references.references.dhReferences.vehicleReferences,
  (state: RootState) => state.references.references.dhReferences.references,
  (
    _state: RootState,
    payload: {
      vehicleKey: string | undefined;
      referenceNumbers?: string[];
    },
  ) => payload,
  (vehicleReferences, references, payload) => {
    if (!payload.referenceNumbers) {
      return [];
    }
    return payload.vehicleKey
      ? (payload.referenceNumbers
          .map((ref) => vehicleReferences.get(payload.vehicleKey as string)?.get(ref))
          .filter((r) => r) as DHReferenceLocal[])
      : (payload.referenceNumbers.map((ref) => references.get(ref)).filter((r) => r) as DHReferenceLocal[]);
  },
);

export const getDHReferencesMap = createSelector(
  (state: RootState) => state.references.references.dhReferences.vehicleReferences,
  (state: RootState) => state.references.references.dhReferences.references,
  (
    _state: RootState,
    payload: {
      vehicleKey: string | undefined;
      referenceNumbers: string[];
    },
  ) => payload,
  (vehicleReferences, references, payload) => {
    const refToSearchIn = payload.vehicleKey ? vehicleReferences.get(payload.vehicleKey) ?? new Map() : references;
    return payload.referenceNumbers.reduce(
      (acc, next) => acc.set(next, refToSearchIn.get(next)),
      new Map<string, DHReferenceLocal | NO_DATA>(),
    );
  },
);

export const getIAMReference = createSelector(
  (state: RootState) => state.references.references.iamReferences.vehicleReferences,
  (state: RootState) => state.references.references.iamReferences.references,
  (
    _state: RootState,
    payload: {
      vehicleKey: string | undefined;
      referenceNumber?: string;
    },
  ) => payload,
  (vehicleReferences, references, payload) => {
    if (!payload.referenceNumber) {
      return undefined;
    }
    return payload.vehicleKey
      ? vehicleReferences.get(payload.vehicleKey)?.get(payload.referenceNumber)
      : references.get(payload.referenceNumber);
  },
);

export const getIAMReferences = createSelector(
  (state: RootState) => state.references.references.iamReferences.vehicleReferences,
  (state: RootState) => state.references.references.iamReferences.references,
  (
    _state: RootState,
    payload: {
      vehicleKey: string | undefined;
      referenceNumbers?: string[];
    },
  ) => payload,
  (vehicleReferences, references, payload) => {
    if (!payload.referenceNumbers) {
      return [];
    }
    return payload.vehicleKey
      ? (payload.referenceNumbers
          .map((ref) => vehicleReferences.get(payload.vehicleKey as string)?.get(ref))
          .filter((r) => r) as IAMReferenceLocal[])
      : (payload.referenceNumbers.map((ref) => references.get(ref)).filter((r) => r) as IAMReferenceLocal[]);
  },
);

export const getIAMReferencesMap = createSelector(
  (state: RootState) => state.references.references.iamReferences.vehicleReferences,
  (state: RootState) => state.references.references.iamReferences.references,
  (
    _state: RootState,
    payload: {
      vehicleKey: string | undefined;
      referenceNumbers: string[];
    },
  ) => payload,
  (vehicleReferences, references, payload) => {
    const refToSearchIn = payload.vehicleKey ? vehicleReferences.get(payload.vehicleKey) ?? new Map() : references;
    return payload.referenceNumbers.reduce(
      (acc, next) => acc.set(next, refToSearchIn.get(next)),
      new Map<string, IAMReferenceLocal | NO_DATA>(),
    );
  },
);

export const getStockInfo = createSelector(
  (state: RootState, referenceNumber?: string, seller?: string) => {
    if (!referenceNumber) {
      return undefined;
    }
    return getStockMapValue(state.references.stockInfoV2.get(referenceNumber), seller);
  },
  (stockInfo) => stockInfo,
);

export const getDmsStockInfo = createSelector(
  (state: RootState, referenceNumber?: string) =>
    referenceNumber ? state.references.dmsStockInfo.get(referenceNumber) : undefined,
  (stockInfo) => stockInfo,
);

export const getStockMapValue = (
  refStocks: SearchData<Map<string, ReferenceStock>> | undefined,
  seller?: string,
): SearchData<ReferenceStock> | undefined => {
  if (!refStocks) {
    return undefined;
  }

  const stockData = getSearchData(refStocks);
  if (stockData && stockData instanceof Map) {
    const defaultStock = seller ? stockData.get(seller) : stockData.values().next().value;
    const isEmpty = defaultStock?.warehouses === undefined || defaultStock?.warehouses.length === 0;
    return { searchStatus: isEmpty ? NOT_FOUND : FOUND, data: defaultStock };
  }
  return { searchStatus: refStocks.searchStatus };
};

export const getIsStockAvailable = createSelector(
  (state: RootState, referenceNumber?: string) =>
    referenceNumber ? state.references.stockInfoV2.get(referenceNumber)?.searchStatus !== NOT_FOUND : true,
  (isStockAvailable) => isStockAvailable,
);

export const getIsDmsStockAvailable = createSelector(
  (state: RootState, referenceNumber?: string) =>
    referenceNumber ? state.references.dmsStockInfo.get(referenceNumber)?.searchStatus !== NOT_FOUND : true,
  (isStockAvailable) => isStockAvailable,
);

export const getIsMprAccepted = createSelector(
  (state: RootState) => state.tires.mprValidation,
  (_state: RootState, reference: string) => reference,
  (mprValidation, reference) => {
    const searchData = mprValidation.get(reference);
    const product = searchData?.data?.products.find((p) => p.reference === reference);
    return product ? product.accepted : undefined;
  },
);

export const getReferencesToStockInfosV2 = createSelector(
  (state: RootState) => state.references.stockInfoV2,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (stockInfo, referenceNumbers) => {
    return referenceNumbers.map((r) => stockInfo.get(r));
  },
);

export const getReferencesToStockInfosSnapshotV2 = createSelector(
  (state: RootState) => state.references.stockInfoSnapshotV2,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (stockInfoSnapshot, referenceNumbers) => {
    return referenceNumbers.map((r) => stockInfoSnapshot.get(r));
  },
);

export const getReferencesToStockInfosSnapshotMapV2 = createSelector(
  (state: RootState) => state.references.stockInfoSnapshotV2,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (stocks, referenceNumbers) => {
    return referenceNumbers.reduce(
      (acc, next) => acc.set(next, stocks.get(next)),
      new Map<string, SearchData<Map<string, ReferenceStock>> | undefined>(),
    );
  },
);

export const getStockUpdateCheckResult = createSelector(
  (state: RootState) => state.references.stockUpdateChecks,
  (_state: RootState, id?: string) => id,
  (stockUpdateChecks, id) => {
    if (id === undefined) {
      return undefined;
    }
    return stockUpdateChecks.get(id);
  },
);

export const getPriceMapValue = (
  refPrices: Map<string, ReferencePriceType> | NO_DATA,
  seller?: string,
): ReferencePriceType | NO_DATA => {
  if (refPrices instanceof Map) {
    return seller ? refPrices.get(seller) : refPrices.values().next().value;
  }
  return refPrices;
};

export const getPrice = createSelector(
  (state: RootState, referenceNumber?: string, seller?: string) => {
    if (!referenceNumber) {
      return undefined;
    }
    return getPriceMapValue(state.references.pricesV2.get(referenceNumber), seller);
  },
  (prices) => prices,
);

export const getPrices = createSelector(
  (state: RootState, referenceNumber?: string) => {
    if (!referenceNumber) {
      return undefined;
    }
    return state.references.pricesV2.get(referenceNumber);
  },
  (prices) => prices,
);

// get stocks v2 for selected reference number
// return SearchData<Map<string, ReferenceStock>> where key is seller or NON_MKTP
export const getStocks = createSelector(
  (state: RootState, referenceNumber?: string) => {
    if (!referenceNumber) {
      return undefined;
    }
    return state.references.stockInfoV2.get(referenceNumber);
  },
  (stocks) => stocks,
);

export const hasMultipleOffers = createSelector(
  (state: RootState) => state.references.pricesV2,
  (state: RootState) => state.references.stockInfoV2,
  (_state: RootState, referenceNumber?: string) => referenceNumber,
  (refPrices, refStocks, refNumber) => {
    if (!refNumber) {
      return false;
    }
    const prices = refPrices.get(refNumber);
    const stocks = getSearchData(refStocks.get(refNumber));

    const hasMultipleStocks = stocks instanceof Map ? stocks.size > 1 : false;
    const hasMultiplePrices = prices instanceof Map ? prices.size > 1 : false;

    return hasMultipleStocks || hasMultiplePrices;
  },
);

export const getRepairPrice = createSelector(
  (state: RootState) => state.references.repairPrices,
  (_state: RootState, referenceNumber?: string) => referenceNumber,
  (repairPrices, referenceNumber) => {
    if (!referenceNumber) {
      return undefined;
    }
    return repairPrices.get(referenceNumber);
  },
);

export const getLoadedPrices = createSelector(
  (state: RootState) => state.references.pricesV2,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (prices, referenceNumbers) => {
    return referenceNumbers.map((reference) => ({
      prices: getPriceMapValue(prices.get(reference)),
      reference,
    }));
  },
);

export const getLoadedPricesMap = createSelector(
  (state: RootState) => state.references.pricesV2,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (prices, referenceNumbers) => {
    const pricesMap = new Map<string, ReferencePriceType | NO_DATA>();
    referenceNumbers.forEach((reference) => pricesMap.set(reference, getPriceMapValue(prices.get(reference))));
    return pricesMap;
  },
);

export const getPricesMap = createSelector(
  (state: RootState) => state.references.pricesV2,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (prices, referenceNumbers) => {
    return referenceNumbers.reduce(
      (acc, next) => acc.set(next, getPriceMapValue(prices.get(next))),
      new Map<string, NO_DATA | ReferencePriceType>(),
    );
  },
);

export const getRepairPricesMap = createSelector(
  (state: RootState) => state.references.repairPrices,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (prices, referenceNumbers) => {
    return referenceNumbers.reduce(
      (acc, next) => acc.set(next, prices.get(next)),
      new Map<string, SearchData<string> | undefined>(),
    );
  },
);

export const getStocksMapV2 = createSelector(
  (state: RootState) => state.references.stockInfoV2,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (stocks, referenceNumbers) => {
    return referenceNumbers.reduce(
      (acc, next) => acc.set(next, stocks.get(next)),
      new Map<string, SearchData<Map<string, ReferenceStock>> | undefined>(),
    );
  },
);

export const getReuseStocksMap = createSelector(
  (state: RootState) => state.references.reuseStock,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (reuseStock, referenceNumbers) => {
    return referenceNumbers.reduce(
      (acc, next) => acc.set(next, reuseStock.get(next)),
      new Map<string, SearchData<ReuseReference> | undefined>(),
    );
  },
);

export const getReuseStocks = createSelector(
  (state: RootState) => state.references.reuseStock,
  (_state: RootState, referenceNumber: string) => referenceNumber,
  (reuseStock, referenceNumber) => reuseStock.get(referenceNumber),
);

export const getReuseAvailabilitiesMap = createSelector(
  (state: RootState) => state.references.reuseAvailabilities,
  (_state: RootState, referenceNumbers: string[]) => referenceNumbers,
  (reuseAvailabilities, referenceNumbers) => {
    return referenceNumbers.reduce(
      (acc, next) => acc.set(next, reuseAvailabilities.get(next)),
      new Map<string, SearchData<ReuseAvailability[]> | undefined>(),
    );
  },
);

export const getReuseAvailabilities = createSelector(
  (state: RootState) => state.references.reuseAvailabilities,
  (_state: RootState, referenceNumber: string) => referenceNumber,
  (reuseAvailabilities, referenceNumber) => reuseAvailabilities.get(referenceNumber),
);

export const getEquivalentReferences = createSelector(
  (state: RootState) => state.references.equivalentReferences,
  (_state: RootState, referenceNumber: string) => referenceNumber,
  (equivalentReferences, referenceNumber) => equivalentReferences.get(referenceNumber),
);

export const getIsExadisEligible = createSelector(
  (state: RootState) => state.references.exadisEligibleReferences,
  (_state: RootState, referenceNumber: string) => referenceNumber,
  (exadisEligibleReferences, referenceNumber) => exadisEligibleReferences.data?.includes(referenceNumber) ?? false,
);

export const getDiscount = createSelector(
  (state: RootState) => state.references.referenceDiscounts,
  (_state: RootState, reference: string) => reference,
  (referenceDiscounts, reference) => referenceDiscounts.get(reference)?.data,
);

export const getDiscounts = createSelector(
  (state: RootState) => state.references.referenceDiscounts,
  (_state: RootState, references: string[]) => references,
  (referenceDiscounts, references) => {
    const discounts = new Map<string, ReferenceDiscount>();
    references.forEach((r) => {
      const disc = referenceDiscounts.get(r);
      if (disc?.data) {
        discounts.set(r, disc.data);
      }
    });
    return discounts;
  },
);

export const getDiscountStatuses = createSelector(
  (state: RootState) => state.references.referenceDiscounts,
  (_state: RootState, references: string[]) => references,
  (referenceDiscounts, references) => {
    const discounts = new Map<string, SEARCH_STATUS>();
    references.forEach((r) => {
      const disc = referenceDiscounts.get(r);
      discounts.set(r, disc?.searchStatus);
    });
    return discounts;
  },
);

export const getReferenceTypes = createSelector(
  (state: RootState) => state.references.referenceToReferenceType,
  (_state: RootState, references: string[]) => references,
  (referenceToReferenceType, references) =>
    references.reduce(
      (acc, next) => acc.set(next, referenceToReferenceType.get(next)),
      new Map<string, ReferenceType | undefined>(),
    ),
);

export const getReferenceSources = createSelector(
  (state: RootState) => state.references.referenceToReferenceSource,
  (_state: RootState, references: string[]) => references,
  (referenceToReferenceSource, references) =>
    references.reduce(
      (acc, next) => acc.set(next, referenceToReferenceSource.get(next)),
      new Map<string, ReferenceSource | undefined>(),
    ),
);

export const getSelectedTab = createSelector(
  (state: RootState) => state.references.selectedTab,
  (selectedTab) => selectedTab,
);

export const getDiscontinuedReferences = createSelector(
  (state: RootState) => state.references.discontinuedReferences,
  (discontinued) => discontinued,
);

export const isDiscontinuedReference = createSelector(
  (state: RootState, referenceNumber: string) => state.references.discontinuedReferences.get(referenceNumber),
  (discontinued) => discontinued,
);

export const getReferenceApplicabilities = createSelector(
  (state: RootState, referenceNumber: string) => state.references.referenceApplicabilities.get(referenceNumber),
  (applicabilities) => applicabilities,
);

export const getReference3dModel = createSelector(
  (state: RootState, referenceNumber: string) => state.references.reference3dModel.get(referenceNumber),
  (model) => model,
);

export const getLatestInStockReference = createSelector(
  [(state: RootState) => state, (_state: RootState, referenceNumber: string) => referenceNumber],
  (state, referenceNumber) => getLatestInStockSupersessionReference(referenceNumber, state),
);

// Export reducer
export default slice.reducer;
